/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {SideNavMaintenanceIcon} from '@ion/dls/src/icons/current/SideNavMaintenanceIcon';
import {IconSize} from '@ion/dls/src/types/types';
import {TertiaryIconButton, TertiaryIconButtonProps} from './TertiaryIconButton';

export interface MaintenanceIconButtonProps extends TertiaryIconButtonProps {}

/**
 * The MaintenanceIconButton is a TertiaryIconButton that displays a
 * wrench/maintenance icon with "Maintenance" text. Icon placement is
 * specified as it would be for the TertiaryIconButton. Other
 * TertiaryIconButton props may also be specified (except "icon"/"label"
 * props, which are fixed).
 *
 * There is no reusable 1:1 click handler for this action inside the DLS
 * package - the legacy `PanelComments` (`@ion/dls/src/components/sections/
 * PanelComments`) accepts its `viewMaintenanceScheduleClick` as a
 * caller-supplied prop, and the concrete handler (`maintenanceSchedule`) is
 * wired at the route level in `AssetDetailsAccordion.tsx`, outside this
 * ticket's scope - so this button simply forwards whatever `onClick` its
 * caller supplies.
 *
 * ```typescript
 *
 *   import React from 'react';
 *   import {MaintenanceIconButton} from '@ion/dls/src/components/buttons/';
 *   ...
 *
 *   function MyComponent {
 *     return (
 *       <React.Fragment>
 *         <MaintenanceIconButton onClick={onMaintenance} iconDirection='left' />
 *         <br />
 *         <MaintenanceIconButton onClick={onMaintenance} />
 *       </React.Fragment>
 *     );
 *   }
 *
 * ```
 *
 */
export const MaintenanceIconButton = (props: MaintenanceIconButtonProps) => {
  return (
    <TertiaryIconButton {...props}
      icon={<SideNavMaintenanceIcon size={IconSize.medium} />}
      label={translate('nav:side.maintenance')} />
  );
};

export default React.memo(MaintenanceIconButton);
